"use client";

import { useState, useCallback } from "react";
import Link from "next/link";
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Info,
  AlertTriangle,
  Calculator,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import { Breadcrumbs } from "@/components/seo/Breadcrumbs";
import { RelatedLinks } from "@/components/seo/RelatedLinks";
import {
  StructuredData,
  generateHowToSchema,
} from "@/components/seo/StructuredData";
import {
  BRUT_ASGARI_UCRET,
  IHBAR_DILIMLERI,
  YILI,
  hesaplaIhbar,
  formatTL,
  type IhbarSonuc,
} from "./ihbar-engine";

const howToSchema = generateHowToSchema({
  title: "İhbar Süresi ve Tazminatı Nasıl Hesaplanır?",
  description:
    "Çalışma sürenize ve brüt maaşınıza göre ihbar sürenizi ve tazminatınızı hesaplayın.",
  totalTime: "PT2M",
  steps: [
    {
      name: "Brüt Maaşı Girin",
      text: "Aylık brüt maaşınızı TL olarak girin.",
    },
    {
      name: "Çalışma Süresini Belirleyin",
      text: "Kaç yıl ve kaç ay çalıştığınızı girin.",
    },
    {
      name: "Sonuçları İnceleyin",
      text: "İhbar sürenizi, brüt/net tazminat tutarınızı ve damga vergisini görüntüleyin.",
    },
  ],
});

function SSSSoru({ soru, cevap }: { soru: string; cevap: string }) {
  const [acik, setAcik] = useState(false);
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden">
      <button
        type="button"
        onClick={() => setAcik((p) => !p)}
        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
        aria-expanded={acik}
      >
        <span className="font-semibold text-gray-900 pr-4">{soru}</span>
        {acik ? (
          <ChevronUp className="w-5 h-5 text-gray-400 shrink-0" />
        ) : (
          <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" />
        )}
      </button>
      {acik && (
        <div className="px-5 pb-5">
          <p className="text-gray-600 leading-relaxed">{cevap}</p>
        </div>
      )}
    </div>
  );
}

export default function IhbarSuresiHesaplayiciClient() {
  const [brutMaas, setBrutMaas] = useState("");
  const [calismaYili, setCalismaYili] = useState("");
  const [calismaAyi, setCalismaAyi] = useState("");
  const [sonuc, setSonuc] = useState<IhbarSonuc | null>(null);
  const [hata, setHata] = useState<string | null>(null);

  const hesapla = useCallback(() => {
    setHata(null);
    const maas = parseFloat(brutMaas);
    const yil = parseInt(calismaYili) || 0;
    const ay = parseInt(calismaAyi) || 0;

    if (!brutMaas || isNaN(maas) || maas <= 0) {
      setHata("Lütfen geçerli bir brüt maaş giriniz.");
      setSonuc(null);
      return;
    }
    if (yil === 0 && ay === 0) {
      setHata("Lütfen çalışma süresini giriniz.");
      setSonuc(null);
      return;
    }

    const hesapSonuc = hesaplaIhbar({
      brutMaas: maas,
      calismaYili: yil,
      calismaAyi: ay,
    });
    setSonuc(hesapSonuc);
  }, [brutMaas, calismaYili, calismaAyi]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        hesapla();
      }
    },
    [hesapla]
  );

  return (
    <>
      <StructuredData data={[howToSchema]} />
      <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
        {/* Hero */}
        <section className="bg-gradient-to-br from-indigo-600 to-purple-700 text-white py-16">
          <div className="max-w-4xl mx-auto px-4">
            <Breadcrumbs
              items={[
                { label: "Araçlar", href: "/araclar" },
                {
                  label: "İhbar Süresi Hesaplayıcı",
                  href: "/araclar/ihbar-suresi-hesaplayici",
                },
              ]}
              className="mb-8 text-white/70"
            />
            <Link
              href="/araclar"
              className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Tüm Araçlar
            </Link>
            <div className="flex items-center gap-4 mb-6">
              <div className="p-4 bg-white/20 rounded-xl backdrop-blur-sm">
                <Clock className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-bold">
                  İhbar Süresi ve Tazminatı Hesaplayıcı
                </h1>
                <span className="inline-block mt-2 px-3 py-1 bg-white/20 rounded-full text-sm font-medium backdrop-blur-sm">
                  İş Kanunu Madde 17
                </span>
              </div>
            </div>
            <p className="text-lg text-white/90 max-w-2xl">
              Kıdeminize göre ihbar sürenizi ve ihbar tazminatınızı hesaplayın.
              Damga vergisi dahil brüt ve net tutarlar.
            </p>
          </div>
        </section>

        {/* İhbar Süreleri Tablosu */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-indigo-50 rounded-2xl p-6 border border-indigo-100">
            <h2 className="text-sm font-semibold text-gray-900 mb-4">
              İhbar Süreleri Tablosu (İş Kanunu Md. 17)
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {IHBAR_DILIMLERI.map((d) => (
                <div
                  key={d.label}
                  className="bg-white rounded-xl p-4 text-center"
                >
                  <span className="text-xs text-gray-500 block mb-1">
                    {d.label}
                  </span>
                  <span className="text-xl font-bold text-indigo-600">
                    {d.haftaSayisi} hafta
                  </span>
                  <span className="text-xs text-gray-400 block mt-1">
                    ({d.gunSayisi} gün)
                  </span>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Hesaplama Formu */}
        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="p-6 sm:p-8">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mb-6">
                <div>
                  <label
                    htmlFor="brutMaas"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Aylık Brüt Maaş
                  </label>
                  <div className="relative">
                    <input
                      id="brutMaas"
                      type="number"
                      inputMode="decimal"
                      min="0"
                      value={brutMaas}
                      onChange={(e) => setBrutMaas(e.target.value)}
                      onKeyDown={handleKeyDown}
                      placeholder="0,00"
                      className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                    />
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-sm text-gray-400">
                      ₺
                    </span>
                  </div>
                  <button
                    type="button"
                    onClick={() => setBrutMaas(BRUT_ASGARI_UCRET.toString())}
                    className="mt-2 text-xs text-indigo-600 hover:text-indigo-700 font-medium"
                  >
                    {YILI} Asgari Ücret ({formatTL(BRUT_ASGARI_UCRET)})
                  </button>
                </div>

                <div>
                  <label
                    htmlFor="calismaYili"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Çalışma Yılı
                  </label>
                  <input
                    id="calismaYili"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="50"
                    value={calismaYili}
                    onChange={(e) => setCalismaYili(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                  />
                </div>

                <div>
                  <label
                    htmlFor="calismaAyi"
                    className="block text-sm font-semibold text-gray-900 mb-2"
                  >
                    Çalışma Ayı
                  </label>
                  <input
                    id="calismaAyi"
                    type="number"
                    inputMode="numeric"
                    min="0"
                    max="11"
                    value={calismaAyi}
                    onChange={(e) => setCalismaAyi(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="0"
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 text-gray-900"
                  />
                </div>
              </div>

              {hata && (
                <div className="mb-6 flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
                  <p className="text-sm text-red-700">{hata}</p>
                </div>
              )}

              <button
                type="button"
                onClick={hesapla}
                className="w-full bg-indigo-600 text-white py-4 rounded-xl font-semibold hover:bg-indigo-700 active:bg-indigo-800 transition-colors shadow-sm"
              >
                <span className="flex items-center justify-center gap-2">
                  <Calculator className="w-5 h-5" />
                  Hesapla
                </span>
              </button>
            </div>

            {/* Sonuç */}
            {sonuc && (
              <div className="border-t border-gray-200 bg-gray-50 p-6 sm:p-8">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                  <div className="bg-indigo-600 text-white rounded-xl p-4">
                    <span className="text-xs text-indigo-100 block">
                      İhbar Süresi
                    </span>
                    <span className="text-2xl font-bold mt-1 block">
                      {sonuc.ihbarSuresiHafta} Hafta
                    </span>
                    <span className="text-xs text-indigo-200 block">
                      ({sonuc.ihbarSuresiGun} gün)
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Brüt Tazminat
                    </span>
                    <span className="text-lg font-bold text-gray-900 mt-1 block tabular-nums">
                      {formatTL(sonuc.brutIhbarTazminati)}
                    </span>
                  </div>
                  <div className="bg-white rounded-xl p-4 border border-gray-200">
                    <span className="text-xs text-gray-500 block">
                      Damga Vergisi
                    </span>
                    <span className="text-lg font-bold text-red-500 mt-1 block tabular-nums">
                      -{formatTL(sonuc.damgaVergisi)}
                    </span>
                    <span className="text-xs text-gray-400 block">%0,759</span>
                  </div>
                  <div className="bg-emerald-50 rounded-xl p-4 border border-emerald-200">
                    <span className="text-xs text-emerald-600 block">
                      Net Tazminat
                    </span>
                    <span className="text-lg font-bold text-emerald-700 mt-1 block tabular-nums">
                      {formatTL(sonuc.netIhbarTazminati)}
                    </span>
                  </div>
                </div>

                <div className="bg-white rounded-xl p-5 border border-gray-200">
                  <h3 className="font-bold text-gray-900 mb-3">
                    Hesaplama Detayı
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Çalışma süresi</span>
                      <span className="font-medium text-gray-900">
                        {Math.floor(sonuc.toplamAy / 12)} yıl{" "}
                        {sonuc.toplamAy % 12} ay
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Dilim</span>
                      <span className="font-medium text-indigo-600">
                        {sonuc.dilim.label}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Günlük brüt ücret</span>
                      <span className="font-medium tabular-nums">
                        {formatTL(sonuc.gunlukBrutUcret)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">
                        İhbar süresi × Günlük ücret
                      </span>
                      <span className="font-medium tabular-nums">
                        {sonuc.ihbarSuresiGun} ×{" "}
                        {formatTL(sonuc.gunlukBrutUcret)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        {/* Rehber */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <div className="prose prose-gray max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              İhbar Süresi ve Tazminatı Rehberi
            </h2>
            <div className="bg-white rounded-2xl p-6 sm:p-8 border border-gray-100 shadow-sm space-y-6">
              <p className="text-gray-700 leading-relaxed">
                4857 sayılı İş Kanunu&apos;nun 17. maddesine göre, belirsiz
                süreli iş sözleşmesini fesheden taraf, karşı tarafa önceden
                bildirimde bulunmak zorundadır. Bu süreye uymayan taraf, ihbar
                tazminatı ödemekle yükümlüdür.
              </p>

              <div>
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Sık Sorulan Sorular
                </h3>
                <div className="space-y-3">
                  <SSSSoru
                    soru="İhbar süresi ne zaman başlar?"
                    cevap="İhbar süresi, fesih bildiriminin karşı tarafa ulaştığı andan itibaren başlar. Bildirim yazılı yapılmalıdır."
                  />
                  <SSSSoru
                    soru="İhbar tazminatı vergiye tabi midir?"
                    cevap="İhbar tazminatı gelir vergisinden muaftır. Sadece binde 7,59 oranında damga vergisine tabidir."
                  />
                  <SSSSoru
                    soru="İhbar süresi ile kıdem tazminatı farkı nedir?"
                    cevap="İhbar süresi, sözleşmeyi fesheden tarafın önceden bildirimde bulunması gereken süredir. Kıdem tazminatı ise çalışılan her yıl için 30 günlük ücret olarak hesaplanan ayrı bir haktır."
                  />
                  <SSSSoru
                    soru="İşçi istifa ederse ihbar tazminatı öder mi?"
                    cevap="Evet, işçi ihbar süresine uymadan istifa ederse, işverene ihbar tazminatı ödemek zorunda kalabilir."
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Bilgilendirme */}
        <section className="max-w-4xl mx-auto px-4 py-4">
          <div className="p-5 bg-amber-50 rounded-xl border border-amber-100">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-amber-600 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-600">
                Bu hesaplama {YILI} yılı İş Kanunu Madde 17 hükümlerine göredir.
                Kesin hesaplama için iş hukuku danışmanınıza başvurunuz.
              </p>
            </div>
          </div>
        </section>

        {/* Related Links */}
        <section className="max-w-4xl mx-auto px-4 py-8">
          <RelatedLinks
            pageType="tool"
            currentSlug="ihbar-suresi-hesaplayici"
            title="İlgili Sayfalar"
            maxLinks={4}
          />
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 py-12">
          <div className="bg-indigo-600 rounded-2xl p-8 text-center text-white">
            <h2 className="text-2xl font-bold mb-4">
              İK Hesaplamalarını Otomatikleştirin
            </h2>
            <p className="text-indigo-100 mb-6 max-w-xl mx-auto">
              IKAI ile kıdem, ihbar, izin hesaplamaları ve tüm İK süreçlerinizi
              tek platformda yönetin.
            </p>
            <Link
              href="/register"
              className="inline-flex items-center gap-2 bg-white text-indigo-700 px-6 py-3 rounded-xl font-semibold hover:bg-indigo-50 transition-colors"
            >
              Ücretsiz Başlayın
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </div>
    </>
  );
}
